import React from 'react';
import { Award, ChevronRight, Star } from 'lucide-react';
import LazyImage from '../common/LazyImage';
import img1 from '../../assets/achievements/skyford_achievements_1.jpeg'
import img2 from '../../assets/achievements/skyford_achievements_2.jpeg'
import img3 from '../../assets/achievements/skyford_achievements_3.jpeg'
import img4 from '../../assets/achievements/skyford_achievements_4.jpeg'
import img5 from '../../assets/achievements/skyford_achievements_5.jpeg'
import img6 from '../../assets/achievements/skyford_achievements_6n.jpeg'

const AchievementsSection = () => {
  const achievements = [
    {
      title: "Best Aviation Institute of the Year",
      years: "2022, 2023, 2024, 2025",
      awardedBy: "Consumer Protection Forum Kerala Awards",
      highlight: "3 consecutive years of excellence"
    },
    {
      title: "Best Aviation Institute of the Year",
      years: "2024, 2025",
      awardedBy: "Business Kerala Magazine",
      highlight: "Setting new industry standards"
    }
  ];

  const imageGallery = [
    { src: img1, alt: "Achievement 1" },
    { src: img2, alt: "Achievement 2" },
    { src: img3, alt: "Achievement 3" },
    { src: img4, alt: "Achievement 4" },
    { src: img5, alt: "Achievement 5" },
    { src: img6, alt: "Achievement 6" },
  ];

  return (
    <div className="py-16 px-6 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-40 left-0 w-96 h-96 bg-red-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-6xl mx-auto relative">
        {/* Innovative Header Section */}
        <div className="mb-20 relative">
          {/* Decorative Elements */}
          <div className="absolute -left-4 top-0 w-8 h-8 bg-red-500/20 rounded-full animate-pulse"></div>
          <div className="absolute right-4 bottom-0 w-6 h-6 bg-red-600/20 rounded-full animate-pulse delay-150"></div>
          
          <div className="text-center relative">
            {/* Small decorative stars */}
            <div className="absolute left-1/4 -top-8">
              <Star className="w-6 h-6 text-red-400 animate-bounce" />
            </div>
            <div className="absolute right-1/4 -top-4">
              <Star className="w-4 h-4 text-red-300 animate-bounce delay-300" />
            </div>

            {/* Main Heading */}
            <div className="relative ">
              <h2 className="text-4xl font-medium bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-red-500 mb-4">
                Our Excellence
              </h2>
              {/* Underline effect */}
              <div className="absolute -bottom-4 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-red-600 transform scale-x-0 group-hover:scale-x-100 animate-expand"></div>
            </div>

            {/* Subtitle with gradient border */}
            <div className="mt-8 relative">
              <div className="relative z-10 px-6 py-2">
                <span className="text-lg text-red-600 font-medium">Setting New Standards in Aviation Education</span>
              </div>
              {/* Gradient background effect */}
              {/* <div className="absolute inset-0 bg-gradient-to-r from-red-50 to-white rounded-lg transform -rotate-1"></div> */}
            </div>
          </div>
        </div>

        {/* Achievements List */}
        <div className="space-y-12 mb-16">
          {achievements.map((achievement, index) => (
            <div 
              key={index}
              className="group relative"
            >
              {/* Achievement Container */}
              <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6 p-8 rounded-2xl bg-gradient-to-r from-red-50 to-white hover:from-red-100 hover:to-red-50 transition-all duration-300 border border-red-100">
                {/* Award Icon */}
                <div className="flex-shrink-0">
                  <div className="w-10 sm:w-16 h-10 sm:h-16 rounded-full bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Award className="w-5 sm:w-8 h-5 sm:h-8 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-grow">
                  <h3 className="text-xl sm:text-2xl font-semibold text-red-600 mb-2">
                    {achievement.title}
                  </h3>
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 text-gray-600 text-sm sm:text-base">
                    <span className="inline-flex items-center">
                      <ChevronRight className="w-4 h-4 mr-2 text-red-500" />
                      Year: {achievement.years}
                    </span>
                    <span className="inline-flex items-center">
                      <ChevronRight className="w-4 h-4 mr-2 text-red-500" />
                      {achievement.awardedBy}
                    </span>
                  </div>
                </div>

                {/* Highlight Badge */}
                <div className="absolute -top-3 right-8 bg-gradient-to-r from-red-500 to-red-600 px-4 py-1 rounded-full">
                  <span className="text-sm font-medium text-white">
                    {achievement.highlight}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Image Gallery Section */}
        <div className="mt-12">
          <h3 className="text-2xl font-medium text-red-600 text-center mb-8">
            Our Achievement Gallery
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1 sm:gap-2 m-auto max-w-6xl">
            {imageGallery.map((image, index) => (
              <div 
                key={index} 
                className="overflow-hidden rounded-lg bg-white p-1 border"
              >
                <LazyImage 
                  src={image.src}
                  alt={image.alt}
                  className="w-full aspect-square rounded-md"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Add the animation keyframe for the expanding underline
const style = document.createElement('style');
style.textContent = `
  @keyframes expand {
    0% { transform: scaleX(0); }
    100% { transform: scaleX(1); }
  }
  .animate-expand {
    animation: expand 1.5s ease-out forwards;
  }
`;
document.head.appendChild(style);

export default AchievementsSection;